import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Divider,
  FileButton,
  Grid,
  Group,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { resolveUploadsUrl } from '@/lib/uploads'
import { ActionButton, PageShell } from '@/components/ui'
import { IconLock, IconUpload } from '@/components/icons'

const AVATAR_MAX_BYTES = 5 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

function getTimezoneOffsetLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date())
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value?.trim()
    if (!raw) return 'GMT'
    if (raw === 'GMT' || raw === 'UTC') return 'GMT+0'
    if (raw.startsWith('GMT')) return raw
    return `GMT ${raw}`
  } catch {
    return 'GMT'
  }
}

function buildTimezoneOptions() {
  const fallback = [
    'UTC',
    'Europe/Moscow',
    'Europe/Berlin',
    'Europe/London',
    'Europe/Kyiv',
    'Asia/Almaty',
    'Asia/Yekaterinburg',
    'Asia/Novosibirsk',
    'Asia/Vladivostok',
    'Asia/Dubai',
    'Asia/Tbilisi',
    'Asia/Baku',
    'America/New_York',
    'America/Los_Angeles',
  ]

  let zones: string[] = fallback
  try {
    const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf
    if (typeof supportedValuesOf === 'function') {
      zones = supportedValuesOf('timeZone')
    }
  } catch {
    zones = fallback
  }
  return zones.map((tz) => ({ value: tz, label: `${tz} (${getTimezoneOffsetLabel(tz)})` }))
}

function initialsFromUser(first: string | null, last: string | null, email: string | null) {
  const a = first?.trim()?.[0]
  const b = last?.trim()?.[0]
  const c = email?.trim()?.[0]
  const s = (a && b ? `${a}${b}` : a || b || c || '?').toUpperCase()
  return s.slice(0, 2)
}

export function ProfilePage() {
  const { user, refreshUser } = useAuth()
  const resetFileRef = useRef<(() => void) | null>(null)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [mattermostLogin, setMattermostLogin] = useState('')
  const [timezone, setTimezone] = useState('UTC')
  const [useBrowserTimezone, setUseBrowserTimezone] = useState(true)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [profileSaving, setProfileSaving] = useState(false)
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const [avatarBroken, setAvatarBroken] = useState(false)
  const [avatarVersion, setAvatarVersion] = useState(0)

  useEffect(() => {
    if (!user) return
    setFirstName(user.first_name ?? '')
    setLastName(user.last_name ?? '')
    setPhone(user.phone ?? '')
    setMattermostLogin(user.mattermost_login ?? '')
    setTimezone(user.timezone ?? 'UTC')
    setUseBrowserTimezone(user.use_browser_timezone !== false)
    setAvatarBroken(false)
  }, [user])

  const avatarSrc =
    user?.avatar_url && !avatarBroken
      ? resolveUploadsUrl(user.avatar_url, { version: `${user.id}-${avatarVersion}` })
      : ''

  const onSaveProfile = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!user) return
      setProfileError(null)
      setProfileSaving(true)
      try {
        await api.updateMyProfile({
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          phone: phone.trim() || null,
          mattermost_login: mattermostLogin.trim() || null,
          timezone: timezone || null,
          use_browser_timezone: useBrowserTimezone,
        })
        await refreshUser()
        showNotification({ title: 'Профиль обновлён', message: 'Изменения сохранены.', color: 'green' })
      } catch (err) {
        setProfileError(err instanceof ApiError ? err.message : 'Не удалось сохранить профиль')
      } finally {
        setProfileSaving(false)
      }
    },
    [user, firstName, lastName, phone, mattermostLogin, timezone, useBrowserTimezone, refreshUser],
  )

  const onAvatar = useCallback(
    async (file: File | null) => {
      if (!file || !user) return
      if (file.size > AVATAR_MAX_BYTES) {
        showNotification({
          title: 'Файл слишком большой',
          message: 'Максимальный размер: 5 МБ.',
          color: 'red',
        })
        resetFileRef.current?.()
        return
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        showNotification({
          title: 'Неподдерживаемый формат',
          message: 'Используйте JPG, PNG, GIF или WebP.',
          color: 'red',
        })
        resetFileRef.current?.()
        return
      }
      setAvatarUploading(true)
      try {
        await api.uploadAvatar(file)
        await refreshUser()
        setAvatarVersion((v) => v + 1)
        setAvatarBroken(false)
        showNotification({ title: 'Аватар обновлён', message: 'Фото профиля сохранено.', color: 'green' })
      } catch (err) {
        showNotification({
          title: 'Ошибка загрузки',
          message: err instanceof ApiError ? err.message : 'Не удалось загрузить фото',
          color: 'red',
        })
      } finally {
        setAvatarUploading(false)
        resetFileRef.current?.()
      }
    },
    [user, refreshUser],
  )

  const onChangePassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setPasswordError(null)
      if (newPassword !== confirmPassword) {
        setPasswordError('Новые пароли не совпадают')
        return
      }
      if (newPassword.length < 8) {
        setPasswordError('Пароль должен содержать минимум 8 символов')
        return
      }
      setPasswordSaving(true)
      try {
        await api.changeMyPassword({
          current_password: currentPassword,
          new_password: newPassword,
        })
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        showNotification({ title: 'Пароль изменён', message: 'Можно продолжать работу.', color: 'green' })
      } catch (err) {
        setPasswordError(err instanceof ApiError ? err.message : 'Не удалось сменить пароль')
      } finally {
        setPasswordSaving(false)
      }
    },
    [currentPassword, newPassword, confirmPassword],
  )

  if (!user) return null

  const cardStyle = {
    borderRadius: 'var(--mantine-radius-lg)',
    border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
    background: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
    boxShadow: 'var(--mantine-shadow-sm)',
  } as const

  return (
    <PageShell>
      <Stack gap="lg" w="100%">
        <Paper component="form" onSubmit={onSaveProfile} p="xl" style={cardStyle}>
          <Stack gap="lg">
            <Group align="flex-start" wrap="nowrap" gap="lg">
              <Avatar
                src={avatarSrc || undefined}
                size={88}
                radius={999}
                color="brand"
                variant="light"
                style={{ flexShrink: 0 }}
                onError={() => setAvatarBroken(true)}
              >
                {!avatarSrc ? initialsFromUser(user.first_name, user.last_name, user.email) : null}
              </Avatar>
              <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <FileButton resetRef={resetFileRef} onChange={onAvatar} accept="image/png,image/jpeg,image/gif,image/webp">
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      color="gray"
                      leftSection={<IconUpload size={16} stroke={1.5} />}
                      loading={avatarUploading}
                    >
                      Загрузить фото
                    </Button>
                  )}
                </FileButton>
                <Text size="xs" c="dimmed" m={0} lh={1.45}>
                  JPG, PNG, GIF или WebP. Не более 5 МБ.
                </Text>
              </Stack>
            </Group>

            {profileError ? (
              <Alert color="red" variant="light" title="Ошибка">
                {profileError}
              </Alert>
            ) : null}

            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Имя" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="Фамилия" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Grid.Col>
            </Grid>

            <TextInput
              label="Email"
              value={user.email ?? ''}
              disabled
              description="Email изменить нельзя"
            />

            <TextInput
              label="Телефон"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />

            <TextInput
              label="Логин в Mattermost"
              value={mattermostLogin}
              onChange={(e) => setMattermostLogin(e.target.value)}
              placeholder="@username"
              description="Для интеграций и упоминаний в Mattermost."
            />

            <Stack gap="xs">
              <Checkbox
                checked={useBrowserTimezone}
                onChange={(e) => setUseBrowserTimezone(e.currentTarget.checked)}
                label="Использовать время с ПК"
                description={`Сейчас на ПК: ${Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}`}
              />
              <Select
                label="Часовой пояс"
                data={buildTimezoneOptions()}
                value={timezone}
                onChange={(v) => setTimezone(v || 'UTC')}
                disabled={useBrowserTimezone}
                searchable
                nothingFoundMessage="Не найдено"
                description="Используется для отображения времени в списке обращений, карточке, сменах и дашбордах."
              />
            </Stack>

            <Box>
              <ActionButton type="submit" action="save" label="Сохранить изменения" loading={profileSaving} />
            </Box>
          </Stack>
        </Paper>

        <Paper component="form" onSubmit={onChangePassword} p="xl" style={cardStyle}>
          <Stack gap="lg">
            <Divider />

            {passwordError ? (
              <Alert color="red" variant="light" title="Ошибка">
                {passwordError}
              </Alert>
            ) : null}

            <PasswordInput
              label="Текущий пароль"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <PasswordInput
              label="Новый пароль"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              description="Минимум 8 символов"
              autoComplete="new-password"
            />
            <PasswordInput
              label="Повторите новый пароль"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />

            <Box>
              <Button
                type="submit"
                color="orange"
                variant="light"
                leftSection={<IconLock size={18} stroke={1.5} />}
                loading={passwordSaving}
              >
                Изменить пароль
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </PageShell>
  )
}
